import "@/views/cabine/editor/fabric-config";
import { fabric } from "fabric";
import {
  criarRecuperacaoDoCanvas,
  redesenharCanvas,
} from "@/utils/restaurarCanvas";

let FontFaceObserver = require("fontfaceobserver");

export function waitForFontReady(fontFamily, timeoutMs = 6000) {
  if (!fontFamily) return Promise.resolve();
  if (document.fonts && document.fonts.check(`1em ${fontFamily}`)) {
    return Promise.resolve();
  }
  const observer = new FontFaceObserver(fontFamily);
  return Promise.race([
    observer.load(),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error("font_timeout")), timeoutMs);
    }),
  ]);
}

export function preloadEditorFonts() {
  ["Montserrat", "Avelyn", "Betrycia", "Rocket"].forEach((fontName) => {
    waitForFontReady(fontName).catch(() => {});
  });
}

// ── Download da foto original ────────────────────────────────────────────────
// A foto era baixada duas vezes (uma sem CORS, outra pelo fabric com CORS)
// sem tratamento de erro: se a primeira falhava o editor ficava para sempre em
// "Importando imagens"; se a segunda falhava o fabric entregava uma imagem
// vazia que entrava no canvas como se fosse a foto. Agora é um download só,
// com CORS, erro e tempo máximo — e a foto só entra no canvas se carregou.

export class ErroCarregamentoImagem extends Error {
  constructor(mensagem, detalhes = {}) {
    super(mensagem);
    this.name = "ErroCarregamentoImagem";
    this.detalhes = detalhes;
  }
}

// Da segunda tentativa em diante a URL ganha um parâmetro único, para o
// navegador não reaproveitar do cache uma resposta que já falhou.
export function urlParaTentativa(src, tentativa = 1) {
  if (!src || tentativa <= 1 || !/^https?:/i.test(src)) return src;
  const separador = src.includes("?") ? "&" : "?";
  return `${src}${separador}_tentativa=${tentativa}-${Date.now()}`;
}

export function carregarImagem(src, { timeoutMs = 40000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(
        new ErroCarregamentoImagem("Foto sem endereço", { causa: "sem_src" })
      );
      return;
    }
    const img = new Image();
    let finalizado = false;
    const finalizar = (callback, valor) => {
      if (finalizado) return;
      finalizado = true;
      clearTimeout(temporizador);
      img.onload = null;
      img.onerror = null;
      callback(valor);
    };
    const falhar = (causa) =>
      finalizar(
        reject,
        new ErroCarregamentoImagem("Não foi possível baixar a foto", { causa })
      );
    const temporizador = setTimeout(() => falhar("tempo_esgotado"), timeoutMs);

    img.onload = () => {
      const largura = img.naturalWidth || img.width;
      const altura = img.naturalHeight || img.height;
      if (largura > 0 && altura > 0) finalizar(resolve, img);
      else falhar("imagem_sem_dimensoes");
    };
    img.onerror = () => falhar("falha_download");
    // Sem CORS o canvas fica "contaminado" e não exporta a foto.
    if (!/^data:/i.test(src)) img.crossOrigin = "anonymous";
    img.src = src;
  });
}

// Canvas que já passaram por dispose() (o editor fechou). O dispose zera o
// contexto 2D do fabric: uma imagem que termina de baixar depois disso não
// pode mais ir para o canvas, senão o renderAll quebra com
// "Cannot read properties of null (reading 'clearRect')".
const canvasDescartados = new WeakSet();

export function canvasFoiDescartado(canvasInstancia) {
  return !canvasInstancia || canvasDescartados.has(canvasInstancia);
}

const versaoRenderMoldura = new WeakMap();

export function renderMoldura(
  canvasInstancia,
  imagemURL,
  firstScreen = false,
  eventoMolduraId = null,
  molduraUsoId = null
) {
  if (canvasFoiDescartado(canvasInstancia)) return Promise.resolve(false);
  const versao =(versaoRenderMoldura.get(canvasInstancia) || 0) + 1;
  versaoRenderMoldura.set(canvasInstancia, versao);
  const canvasWidth = canvasInstancia.getWidth();
  const canvasHeight = canvasInstancia.getHeight();
  const objetosDoCanvas = canvasInstancia.getObjects();

  if (!firstScreen) {
    for (let i = objetosDoCanvas.length - 1; i >= 0; i--) {
      const item = objetosDoCanvas[i];
      if (item.toObject().tipo === "moldura") {
        canvasInstancia.remove(item);
      }
    }
  }
  canvasInstancia.renderAll();

  if (!imagemURL) return Promise.resolve(true);

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      if (versaoRenderMoldura.get(canvasInstancia) === versao) {
        versaoRenderMoldura.set(canvasInstancia, versao + 1);
      }
      resolve(false);
    }, 10000);
    try {
      fabric.Image.fromURL(
        `${imagemURL}`,
        (img, isError) => {
          if (
            isError ||
            !img ||
            !Number.isFinite(img.width) ||
            !Number.isFinite(img.height) ||
            img.width <= 0 ||
            img.height <= 0 ||
            versaoRenderMoldura.get(canvasInstancia) !== versao ||
            canvasFoiDescartado(canvasInstancia)
          ) {
            clearTimeout(timeout);
            resolve(false);
            return;
          }
          img.set({
            left: 0,
            top: 0,
            selectable: false,
            hasControls: false,
            evented: false,
            originX: "left",
            originY: "top",
          });
          img.eventoMolduraId = eventoMolduraId;
          img.eventoMolduraUrl = eventoMolduraId ? imagemURL : null;

          const imgWidth = img.width;
          const imgHeight = img.height;
          const scaleX = canvasWidth / imgWidth;
          const scaleY = canvasHeight / imgHeight;
          const scale = Math.max(scaleX, scaleY);

          img.scale(scale);
          img.set({
            left: (canvasWidth - imgWidth * scale) / 2,
            top: (canvasHeight - imgHeight * scale) / 2,
          });

          img.toObject = function () {
            return {
              tipo: "moldura",
              eventoMolduraId,
              molduraUsoId,
              eventoMolduraUrl: eventoMolduraId ? imagemURL : null,
            };
          };

          canvasInstancia.add(img);
          canvasInstancia.moveTo(img, 1);
          canvasInstancia.discardActiveObject();
          canvasInstancia.renderAll();
          clearTimeout(timeout);
          resolve(true);
        },
        { crossOrigin: "anonymous" }
      );
    } catch (error) {
      clearTimeout(timeout);
      resolve(false);
    }
  });
}

export function useCanvasEditor({ formato, callbacks }) {
  // callbacks = { onItemSelecionado, onDiscartaSelect, onModalInterfaceLoading,
  //   getMolduraDefault }
  const canvas = [];

  let isGesturing = false;
  let lastDistance = 0;

  function getDistance(p1, p2) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }

  function getCenter(p1, p2) {
    return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  }

  function iniciaMolduraDefault(indexCanvas) {
    // Quem monta o editor pode decidir a moldura de cada foto: no evento ela
    // depende da orientação da foto, que o formato não conhece.
    const molduraDefault = callbacks.getMolduraDefault
      ? callbacks.getMolduraDefault(indexCanvas)
      : formato.defaultMoldura;
    if (molduraDefault) {
      const canvasInst = canvas[indexCanvas].instancia;
      renderMoldura(canvasInst, molduraDefault.url, true);
    }
  }

  // Baixa a foto e coloca no canvas. Rejeita (ErroCarregamentoImagem) se não
  // carregar — nesse caso nada entra no canvas.
  async function gerarFotoNoCanvas(
    referencia,
    index,
    onDone,
    { tentativa = 1, timeoutMs } = {}
  ) {
    const canvasAtual = canvas[index].instancia;
    // O tamanho é o do canvas como ele está: retrato é o do formato, mas a
    // foto que está (ou já começa) em paisagem precisa cobrir o canvas deitado.
    const canvasSize = {
      width: canvasAtual.getWidth(),
      height: canvasAtual.getHeight(),
    };

    const elemento = await carregarImagem(
      urlParaTentativa(referencia && referencia.src, tentativa),
      { timeoutMs }
    );
    // O editor pode ter fechado durante o download.
    if (canvasFoiDescartado(canvasAtual)) return null;

    const img = new fabric.Image(elemento);
    // Para baixar a foto de novo se o navegador apagar o canvas.
    img.fotoSrc = referencia && referencia.src;
    img.set({ left: 0, top: 0 });
    formato.fitImageToCanvas(img, canvasSize);
    img.toObject = function () {
      return { tipo: "foto", width: img.width, height: img.height };
    };
    canvasAtual.add(img);
    canvasAtual.renderAll();
    img.center();
    iniciaMolduraDefault(index);

    if (onDone) onDone(index);
    return img;
  }

  // "Desfazer tudo": volta o canvas à foto original. Antes o canvas era
  // limpo ANTES de baixar a foto, sem tratar erro: se o download falhasse a
  // foto não voltava (canvas só com o fundo branco) e o botão ficava girando
  // para sempre; se o fabric falhasse, entrava uma imagem vazia como a foto.
  // Agora a foto é baixada primeiro (com tentativas e tempo máximo) e o canvas
  // só é limpo depois. Se não baixar, rejeita e o canvas fica como estava.
  async function gerarFotoNoCanvasZerada(
    referencia,
    index,
    onDone,
    { tentativas = 3, timeoutMs = 15000 } = {}
  ) {
    const canvasAtual = canvas[index].instancia;
    const src = referencia && referencia.src;

    let elemento = null;
    let ultimoErro = null;
    for (let tentativa = 1; tentativa <= tentativas && !elemento; tentativa++) {
      try {
        elemento = await carregarImagem(urlParaTentativa(src, tentativa), {
          timeoutMs,
        });
      } catch (erro) {
        ultimoErro = erro;
      }
    }
    if (!elemento) throw ultimoErro;
    // O editor pode ter fechado durante o download.
    if (canvasFoiDescartado(canvasAtual)) return null;

    // O tamanho é o do canvas como ele está: retrato é o do formato, mas a
    // foto que está (ou já começa) em paisagem precisa cobrir o canvas deitado.
    const canvasSize = {
      width: canvasAtual.getWidth(),
      height: canvasAtual.getHeight(),
    };
    canvasAtual.clear();
    canvasAtual.backgroundColor = "rgba(255,255,255,1)";

    const img = new fabric.Image(elemento);
    img.fotoSrc = src;
    img.set({ left: 0, top: 0 });
    formato.fitImageToCanvas(img, canvasSize);
    img.toObject = function () {
      return { tipo: "foto", width: img.width, height: img.height };
    };
    canvasAtual.add(img);
    canvasAtual.renderAll();
    img.center();
    iniciaMolduraDefault(index);

    if (onDone) onDone(index);
    return img;
  }

  function criarInstanciasDoCanvas(referencias) {
    const { width: canvasWidth, height: canvasHeight } = formato.canvasSize;

    referencias.forEach((item, index) => {
      const referenciaCanvas = "canvas" + index;
      const canvasFabric = new fabric.Canvas(referenciaCanvas, {
        width: canvasWidth,
        height: canvasHeight,
        allowTouchScrolling: false,
        backgroundColor: "rgba(255,255,255,1)",
        selection: true,
      });

      canvas[index] = {
        instancia: {},
        propriedades: {},
        _touchHandlers: {},
      };
      canvas[index].instancia = canvasFabric;
      canvas[index].propriedades = {
        url_editada: item.url_editada,
        url_original: item.url_original,
      };

      // Sem memória de vídeo o Chrome apaga o canvas e avisa com
      // "contextlost"; quando devolve ("contextrestored") o canvas está vazio.
      // O fabric não trata isso: redesenha a partir do que está na memória.
      const aoPerderContexto = () => {
        console.warn("Canvas do editor perdeu o conteúdo", { index });
        if (callbacks.onContextoPerdido) callbacks.onContextoPerdido(index);
      };
      const aoRestaurarContexto = () => redesenharCanvas(canvasFabric);
      canvasFabric.lowerCanvasEl.addEventListener(
        "contextlost",
        aoPerderContexto
      );
      canvasFabric.lowerCanvasEl.addEventListener(
        "contextrestored",
        aoRestaurarContexto
      );
      canvas[index]._contextoHandlers = {
        aoPerderContexto,
        aoRestaurarContexto,
      };

      canvas[index].instancia.on("selection:created", () => {
        if (callbacks.onItemSelecionado) callbacks.onItemSelecionado(index);
      });
      canvas[index].instancia.on("selection:updated", () => {
        if (callbacks.onItemSelecionado) callbacks.onItemSelecionado(index);
      });
      canvas[index].instancia.on("selection:cleared", () => {
        if (callbacks.onDiscartaSelect) callbacks.onDiscartaSelect(index);
      });
      canvas[index].instancia.on("object:moving", () => {
        if (callbacks.onDiscartaSelect) callbacks.onDiscartaSelect(index);
      });

      // zoom via mouse wheel
      canvas[index].instancia.on("mouse:wheel", (opt) => {
        const evt = opt.e;
        const itemZero = canvas[index].instancia.item(0);
        evt.preventDefault();
        evt.stopPropagation();
        const delta = evt.deltaY;
        let zoom = itemZero.scaleX;
        zoom *= 0.999 ** delta;
        if (zoom > 20) zoom = 20;
        if (zoom < 0.01) zoom = 0.01;
        itemZero.set({ scaleX: zoom, scaleY: zoom });
        canvas[index].instancia.renderAll();
      });

      // touch pinch-to-zoom
      // Os handlers usam `canvasFabric` e não `canvas[index]`: o navegador
      // ainda entrega toques de um gesto em andamento depois que o editor
      // fecha, e aí `canvas` já foi esvaziado por destruirInstancias.
      const aoIniciarToque = (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          isGesturing = true;
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];
          lastDistance = getDistance(
            { x: touch1.clientX, y: touch1.clientY },
            { x: touch2.clientX, y: touch2.clientY }
          );
          canvasFabric.selection = false;
        }
      };

      const aoMoverToque = (e) => {
        const targetObject = canvasFabric.item(0);
        if (e.touches.length === 2 && isGesturing && targetObject) {
          e.preventDefault();
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];
          const currentDistance = getDistance(
            { x: touch1.clientX, y: touch1.clientY },
            { x: touch2.clientX, y: touch2.clientY }
          );
          const scaleFactor = currentDistance / lastDistance;
          const newScaleX = targetObject.scaleX * scaleFactor;
          const newScaleY = targetObject.scaleY * scaleFactor;
          const minScale = 0.1;
          const maxScale = 5;
          const clampedScaleX = Math.max(
            minScale,
            Math.min(maxScale, newScaleX)
          );
          const clampedScaleY = Math.max(
            minScale,
            Math.min(maxScale, newScaleY)
          );
          targetObject.set({ scaleX: clampedScaleX, scaleY: clampedScaleY });
          lastDistance = currentDistance;
          canvasFabric.renderAll();
          canvasFabric.fire("object:scaling", {
            target: targetObject,
            transform: { scaleX: clampedScaleX, scaleY: clampedScaleY },
          });
        }
      };

      const aoTerminarToque = (e) => {
        const targetObject = canvasFabric.item(0);
        if (e.touches.length < 2) {
          isGesturing = false;
          if (targetObject) {
            canvasFabric.fire("object:modified", { target: targetObject });
          }
          setTimeout(() => {
            canvasFabric.selection = true;
          }, 100);
        }
      };

      const elementoToque = canvasFabric.upperCanvasEl;
      elementoToque.addEventListener("touchstart", aoIniciarToque);
      elementoToque.addEventListener("touchmove", aoMoverToque);
      elementoToque.addEventListener("touchend", aoTerminarToque);
      canvas[index]._touchHandlers = {
        elemento: elementoToque,
        touchstart: aoIniciarToque,
        touchmove: aoMoverToque,
        touchend: aoTerminarToque,
      };
    });
  }

  function destruirInstancias(referencias) {
    referencias.forEach((item, index) => {
      canvasDescartados.add(canvas[index].instancia);
      // O dispose do fabric tira o upperCanvasEl da página, mas os listeners
      // de toque continuariam presos a ele.
      const toque = canvas[index]._touchHandlers;
      if (toque && toque.elemento) {
        ["touchstart", "touchmove", "touchend"].forEach((tipo) =>
          toque.elemento.removeEventListener(tipo, toque[tipo])
        );
      }
      const handlers = canvas[index]._contextoHandlers;
      const elemento = canvas[index].instancia.lowerCanvasEl;
      if (handlers && elemento) {
        elemento.removeEventListener("contextlost", handlers.aoPerderContexto);
        elemento.removeEventListener(
          "contextrestored",
          handlers.aoRestaurarContexto
        );
      }
      canvas[index].instancia.clear();
      canvas[index].instancia.dispose();
      canvas[index].instancia.off("selection:created");
      canvas[index].instancia.off("selection:updated");
    });
    canvas.length = 0;
  }

  // Chamada entre as tentativas de exportar a foto do canvas `index` quando a
  // anterior saiu branca: redesenha e, se não bastar, baixa a foto de novo.
  function recuperarCanvas(index, tentativaQueFalhou) {
    const canvasInst = canvas[index] && canvas[index].instancia;
    if (!canvasInst || !canvasInst.getObjects) return Promise.resolve();
    console.warn("Exportação da foto saiu branca; recuperando o canvas", {
      index,
      tentativaQueFalhou,
    });
    const recuperar = criarRecuperacaoDoCanvas(canvasInst, (src, tentativa) =>
      carregarImagem(urlParaTentativa(src, tentativa), { timeoutMs: 10000 })
    );
    return recuperar(tentativaQueFalhou);
  }

  function selecionaImagemZero(indexAtual) {
    return canvas[indexAtual].instancia.item(0);
  }

  function centralizarImagem(indexAtual, itemSelecionado) {
    itemSelecionado.center();
    canvas[indexAtual].instancia.renderAll();
  }

  function flipImagem(direction, indexAtual, itemSelecionado) {
    if (direction == "horizontal") {
      itemSelecionado.flipX = !itemSelecionado.flipX;
    } else {
      itemSelecionado.flipY = !itemSelecionado.flipY;
    }
    canvas[indexAtual].instancia.renderAll();
  }

  function rotateImagem(direction, indexAtual, itemSelecionado) {
    const anguloAtual = itemSelecionado.angle;
    if (direction == "left") {
      itemSelecionado.rotate(anguloAtual - 15);
    } else {
      itemSelecionado.rotate(anguloAtual + 15);
    }
    canvas[indexAtual].instancia.renderAll();
  }

  function criarTextoNoCanvasAtual(config, indexAtual) {
    const textPayload = config.text;
    if (!config || !config.text || config.text == "") return false;
    const canvasInst = canvas[indexAtual].instancia;
    const fontFamily = config.font || "Montserrat";
    const addText = () => {
      const elementText = new fabric.Textbox(`${config.text}`, {
        left: 20,
        top: 170,
        fontSize: 40,
        fontFamily: fontFamily,
        fill: config.color || "#000000",
        editable: false,
      });
      elementText.toObject = function () {
        return { tipo: "texto", text: `${textPayload}` };
      };
      canvasInst.add(elementText);
      canvasInst.setActiveObject(elementText);
      canvasInst.requestRenderAll();
    };
    waitForFontReady(fontFamily).then(addText).catch(addText);
  }

  function criarEmojiNoCanvasAtual(item, indexAtual) {
    const canvasInst = canvas[indexAtual].instancia;
    const elementEmoticon = new fabric.Textbox(`${item}`, {
      left: 20,
      top: 170,
      fontSize: 120,
    });
    elementEmoticon.toObject = function () {
      return { tipo: "emoji", text: `${item}` };
    };
    canvasInst.add(elementEmoticon);
    canvasInst.setActiveObject(elementEmoticon);
  }

  function removerItem(indexAtual, mostrarErroFn) {
    const canvasInst = canvas[indexAtual].instancia;
    const object = canvasInst.getActiveObject();
    if (!object) {
      if (mostrarErroFn) mostrarErroFn("Selecione o item que deseja remover.");
      return false;
    }
    canvasInst.remove(object);
    canvasInst.renderAll();
    return true;
  }

  function removerTodosOsItemsDesseTipo(name, indexAtual) {
    const canvasInst = canvas[indexAtual].instancia;
    const listaObjetos = canvasInst.getObjects();
    listaObjetos.filter((item) => {
      if (item.toObject().tipo == name) {
        canvasInst.remove(item);
        canvasInst.renderAll();
      }
    });
  }

  function deleteItemForItem(item, indexAtual) {
    canvas[indexAtual].instancia.remove(item);
    canvas[indexAtual].instancia.renderAll();
  }

  function validaExistenciaDeMoldura(indexAtual) {
    const canvasInst = canvas[indexAtual].instancia;
    const objetosDoCanvas = canvasInst.getObjects();
    objetosDoCanvas.forEach((item) => {
      const bin = item.toObject();
      if (bin.tipo == "moldura") {
        canvasInst.remove(item);
        canvasInst.renderAll();
      }
    });
  }

  function mandaImagemParaTras(indexAtual) {
    const canvasInst = canvas[indexAtual].instancia;
    const [imgObject] = canvasInst.getObjects();
    imgObject.sendToBack();
    canvasInst.renderAll();
  }

  function resetZoom(indexAtual) {
    canvas[indexAtual].instancia.setViewportTransform([1, 0, 0, 1, 0, 0]);
  }

  function validacaoFotoAtual(indexAtual) {
    let found = null;
    const objetos = canvas[indexAtual].instancia.getObjects();
    objetos.forEach((item) => {
      const propriedades = item.toObject();
      if (propriedades && propriedades.tipo && propriedades.tipo == "foto") {
        found = item;
      }
    });
    return found;
  }

  function selecionouItem(indexAtual) {
    const canvasInst = canvas[indexAtual].instancia;
    const itensForCanvas = canvasInst.getObjects();
    const selectedItem = canvasInst.getActiveObject();
    return itensForCanvas.find((item) => item == selectedItem);
  }

  function discartaSelect(indexAtual, itemSelecionado) {
    const canvasInst = canvas[indexAtual].instancia;
    canvasInst.discardActiveObject(itemSelecionado);
    canvasInst.renderAll();
  }

  return {
    canvas,
    criarInstanciasDoCanvas,
    destruirInstancias,
    recuperarCanvas,
    gerarFotoNoCanvas,
    gerarFotoNoCanvasZerada,
    iniciaMolduraDefault,
    renderMoldura,
    selecionaImagemZero,
    centralizarImagem,
    flipImagem,
    rotateImagem,
    criarTextoNoCanvasAtual,
    criarEmojiNoCanvasAtual,
    removerItem,
    removerTodosOsItemsDesseTipo,
    deleteItemForItem,
    validaExistenciaDeMoldura,
    mandaImagemParaTras,
    resetZoom,
    validacaoFotoAtual,
    selecionouItem,
    discartaSelect,
    getDistance,
    getCenter,
  };
}
